
import Multiselect from 'multiselect-react-dropdown';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import Popover from 'react-bootstrap/Popover';
import { BiMessageAltDetail, BiShareAlt, BiUpload } from 'react-icons/bi';
import { BsQuestionCircle } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { GlbFormContext, LoaderContext, CompanyContext } from '../../App';
import NumericAnswerField from '../../components/Answer/NumericAnswerField';
import FormFooter from '../../components/Form/FormFooter';
import FormHeader from '../../components/Form/FormHeader';
import FormSidebar from '../../components/Form/FormSidebar';
import { QuestionType } from '../../core/constants/common';
import useBaseForm from '../../core/hooks/useBaseForm';
import FormService from '../../core/services/FormService';
import FormEditService from '../../core/services/FromEditService';
import ConfirmDialog from '../../core/utilities/ConfirmPopup';
import Utils from '../../core/utilities/Utils';
import ShareFormModal from "../ShareFormModal/ShareFormModal";
import '../FormSubmit/FormSubmit.scss';
import GridTable from '../../components/RadioGridView/GridTable';
import DataTable from '../../components/TableView/DataTable';
import GridQuestionView from '../../components/GridQuestionView/GridQuestionView';
import { flattenFormFields, getInputTypeForFieldType } from '../../core/utilities/gridQuestionUtils';
import { getVisibleQuestionNumber } from '../../core/utilities/questionNumbering';
import { FormContext } from '../../App';
import PreviewShell from '../../components/layout/PreviewShell';
import '../../design-system/index.scss';
import '../../components/ui/ui.scss';

const useNewBuilder = process.env.REACT_APP_NEW_BUILDER !== 'false';

const FormPreview = () => {
  const { langId } = useParams();
  const { id } = useParams();
  const [activeSectionData, setActiveSectionData] = useState({ id: 0 });
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const canvasRef = useRef(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const { setLoaderState } = useContext(LoaderContext)
  const { glbForm, setGlbForm } = useContext(GlbFormContext);
  const { companyId, userFullName } = useContext(CompanyContext);
  const [unitsOfMeasure, setUnitsOfMeasure] = useState([]);
  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [fileUploadEnable, setFileUploadEnable] = useState({ enabled: false, itemId: null });
  const [showComment, setShowComment] = useState({});
  const { formObj } = useContext(FormContext);
  const editPermissions = formObj?.Permissions;
  const [shareModalData, setShareModalData] = useState(null);
  const {
    formatPreviewResponseForApplyingLogics,
    applyLogicDefinitonsOnFieldResponse,
    getInitializedHistResponseArrayFor,
    initializeResponseForField,
    initializeFormQuestions,
    initializeHistResponseArrayFor,
    initializeFormQuestionsForHistResponse,
    formdata, setFormData,
    responseData, setResponseData,
    histResponseData, setHistResponseData,
    checkValidationError,
    updateOptionSelectionValidation,
    getOptionValidationMessage,
    shouldDisableOption
  } = useBaseForm();

  useEffect(() => {
    getPreviewFormDetails(langId);
  }, [langId]);

  useEffect(() => {
    if (companyId >= 0) {
      loadUnitsOfMeasure();
    }
  }, [companyId]);

  useEffect(() => {
    const scrollToTarget = (selector) => {
      const el = canvasRef.current?.querySelector(selector);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    const timers = [];
    const attemptScroll = () => {
      if (activeQuestionId != null) {
        return scrollToTarget(`[data-field-id="${activeQuestionId}"]`);
      }
      if (activeSectionData?.id != null) {
        return scrollToTarget(`[data-section-id="${activeSectionData.id}"]`);
      }
      return false;
    };

    let retryCount = 0;
    const scheduleRetry = (delay) => {
      if (retryCount >= 4) return;
      retryCount += 1;
      timers.push(setTimeout(() => {
        if (!attemptScroll()) scheduleRetry(200);
      }, delay));
    };

    const frame = requestAnimationFrame(() => {
      if (!attemptScroll()) scheduleRetry(150);
    });
    timers.push(frame);

    return () => {
      timers.forEach((id) => {
        cancelAnimationFrame(id);
        clearTimeout(id);
      });
    };
  }, [activeQuestionId, activeSectionData, formdata]);

  const findInitialActiveSection = (data) => {
    if (data.fields?.length > 0) {
      return { id: 0, fields: data.fields };
    }
    if (data.sections?.length > 0) {
      const firstSection = [...data.sections].sort((a, b) => a.sortOrder - b.sortOrder)[0];
      if (firstSection.fields?.length > 0) return firstSection;
      if (firstSection.subSections?.length > 0) return firstSection.subSections[0];
    }
    return { id: 0, fields: data.fields };
  };

  const getPreviewFormDetails = (langId) => {
    setLoaderState({ display: true, type: 'TEXTUAL' });
    FormEditService.getPreviewFormDetails(id, langId)
      .then((response) => {
        setLoaderState({ display: false });
        const data = formatPreviewResponseForApplyingLogics(response.data);
        setFormData(data);
        setGlbForm(data);
        setActiveSectionData(findInitialActiveSection(data));
        setActiveQuestionId(null);

        // Initialize responses for every field across the whole form tree (root fields,
        // sections and subsections), since Preview now renders the entire form in one
        // continuous scroll instead of one section at a time.
        const initArray = [];
        data?.fields?.forEach((field) => {
          const obj = { formField: { id: field.id } };
          initializeResponseForField(field, obj);
          initArray.push(obj);
        });
        initializeFormQuestions(initArray, data?.sections);
        setResponseData(initArray);

        const histResArray = {};
        initializeHistResponseArrayFor(data?.fields, data, histResArray);
        initializeFormQuestionsForHistResponse(histResArray, data?.sections, data);
        setHistResponseData(histResArray);
      })
      .catch((error) => {
        setLoaderState({ display: false });
      });
  };

  const initializeRootAsActiveSection = (data) => {
    const formSkeleton = data ?? formdata;
    setActiveSectionData({
      id: 0,
      fields: formSkeleton.fields
    });
    setActiveQuestionId(null);
    const responseArray = getInitializedResponseArrayFor(formSkeleton.fields);
    setResponseData(responseArray);
    const histResArray = getInitializedHistResponseArrayFor(formSkeleton.fields, formSkeleton);
    setHistResponseData(histResArray);
  };

  const setSectionDetails = (sectionId, sections, form = null) => {
    const formSkeleton = form ?? formdata;
    const section = sections.find(x => x.id === sectionId);
    if (!section) return;

    setActiveSectionData(section);
    setActiveQuestionId(null);
    const responseArray = getInitializedResponseArrayFor(section.fields);
    setResponseData(responseArray);
    const histResArray = getInitializedHistResponseArrayFor(section.fields, formSkeleton);
    setHistResponseData(histResArray);
  }

  const onSelectQuestion = (fieldId) => {
    setActiveQuestionId(fieldId);
  };

  const publishConfirm = () => {
    FormEditService.publishForm(id)
      .then((response) => {
        toast.success("Success", {
          position: toast.POSITION.TOP_RIGHT,
        });
        const url = Utils.getFormattedFormBuilderExitURL();
        if (!url) return;

        window.location.href = url;
      })
      .catch((error) => {
      });
  };

  const onSelectDeSelectML = (selectedList, selectedItem, index, item) => {
    let mulObjArr = [];
    let arr = [...responseData];
    for (let i = 0; i < selectedList.length; i++) {
      mulObjArr.push({
        id: parseInt(selectedList[i].id),
        name: item.options?.find(o => o.id === +selectedList[i].id)?.name
      });
    }
    arr[index]['responseOptions'] = mulObjArr;
    arr[index]['responseOptionsShow'] = selectedList;
    // Validate option selection for MULTIPLE_CHOICE if applicable
    if (item.type === QuestionType.MULTIPLE_CHOICE) {
      const selectedCount = mulObjArr?.length || 0;
      updateOptionSelectionValidation(item, selectedCount);
    }
    setResponseData(arr);
    const fDataClone = JSON.parse(JSON.stringify(formdata));
    applyLogicDefinitonsOnFieldResponse({
      field: item,
      index: index,
      responseArray: arr,
      forPreview: true,
      histResponseArray: histResponseData,
      formSkeleton: fDataClone
    });
    setFormData(fDataClone);
  }

  const onResponseChange = (index, item, isUnit = false) => (e) => {
    let isOtherOption = false;
    if (item.type === 'RADIO' || item.type === 'DROP_DOWN' || item.type === 'MULTIPLE_CHOICE') {
      isOtherOption = e.target.value === 'other';
    }
    let sendObj = null;
    const responseArray = JSON.parse(JSON.stringify(responseData));
    if (item.type === 'MULTI_SELECT_DROP_DOWN') {
      let mulObjArr = [];
      for (let i = 0; i < item.selectedList.length; i++) {
        let objId = {
          id: parseInt(item.id)
        }
        mulObjArr.push(objId);
      }
      responseArray[index]['responseOptions'] = mulObjArr;
    } else {
      if (e.target.value != null && e.target.value != undefined && e.target.value !== '') {
        sendObj = {
          id: isOtherOption ? e.target.value : parseInt(e.target.value) || -1
        };
      }

      if (item.type === 'SINGLE_TEXTBOX' || item.type === 'TEXT_BOX') {
        responseArray[index]['textResponse'] = e.target.value;
      } else if (item.type === 'RADIO' || item.type === 'DROP_DOWN') {
        if (sendObj != null) {
          sendObj = {
            ...sendObj,
            name: item.options?.find(o => o.id === (+e.target.value || -1))?.name
          };
          responseArray[index]['responseOptions'] = [sendObj];
        }
      } else if (item.type === 'MULTI_SELECT_DROP_DOWN' || item.type === 'MULTIPLE_CHOICE') {
        let indexFilter = responseArray[index]['responseOptions'].findIndex(x => x.id == e.target.value);
        if (indexFilter !== -1) {
          responseArray[index]['responseOptions'].splice(indexFilter, 1);
        } else {
          if (sendObj != null) {
            sendObj = {
              ...sendObj,
              name: item.options?.find(o => o.id === (+e.target.value || -1))?.name
            };
            responseArray[index]['responseOptions'].push(sendObj);
          }
        }
        // Validate option selection for MULTIPLE_CHOICE
        if (item.type === QuestionType.MULTIPLE_CHOICE) {
          const selectedCount = responseArray[index]['responseOptions']?.length || 0;
          updateOptionSelectionValidation(item, selectedCount);
        }
      } else if (item.type === 'DATE_TIME_BOX') {
        responseArray[index]['dateResponse'] = e.target.value;
      } else if (item.type === 'DATE_RANGE') {
        if (e.target.id === "dateResponse") {
          responseArray[index]['dateResponse'] = e.target.value;
        }
        else {
          responseArray[index]['toDateResponse'] = e.target.value;
        }

      } else if (item.type === 'FILE_UPLOAD') {
        responseArray[index]['files'] = [e.target.value];
      } else if (item.type === 'NUMERIC_VALUE') {
        if (isUnit) {
          responseArray[index]['uom'] = e.target.value;
        } else {
          responseArray[index]['numericResponse'] = e.target.value === '' ? '' : +e.target.value;
        }
      }
    }
    setResponseData(responseArray);
    if (isOtherOption) return;


    const fDataClone = JSON.parse(JSON.stringify(formdata));
    applyLogicDefinitonsOnFieldResponse({
      field: item,
      index: index,
      responseArray: responseArray,
      histResponseArray: histResponseData,
      forPreview: true,
      formSkeleton: fDataClone
    });
    setFormData(fDataClone);
  }

  // Mirrors FormSubmit's buildGridAnswerContext: a GRID question never owns
  // an answer itself, each cell just reads/writes its linked field's own
  // responseData entry via the same onResponseChange used everywhere else.
  const buildGridAnswerContext = () => {
    const allFields = flattenFormFields(formdata);
    return {
      readOnly: false,
      getDisplayValue: (linkedFieldId) => {
        const linkedField = allFields.find((f) => f.id === linkedFieldId);
        const entry = responseData.find((r) => r.formField.id === linkedFieldId);
        if (!linkedField || !entry) return '';
        if (linkedField.type === QuestionType.NUMERIC_VALUE) return entry.numericResponse ?? '';
        return entry.textResponse ?? '';
      },
      getInputType: (linkedFieldId) => getInputTypeForFieldType(allFields.find((f) => f.id === linkedFieldId)?.type),
      getChangeHandler: (linkedFieldId) => {
        const linkedField = allFields.find((f) => f.id === linkedFieldId);
        const linkedIndex = responseData.findIndex((r) => r.formField.id === linkedFieldId);
        if (!linkedField || linkedIndex === -1) return undefined;
        return onResponseChange(linkedIndex, linkedField);
      },
      renderExtra: (linkedFieldId) => {
        const linkedField = allFields.find((f) => f.id === linkedFieldId);
        const linkedIndex = responseData.findIndex((r) => r.formField.id === linkedFieldId);
        if (!linkedField || linkedIndex === -1
          || linkedField.type !== QuestionType.NUMERIC_VALUE || !linkedField.uomRequired) return null;
        const relevantUoms = linkedField.relevantUoms;
        return (
          <select
            className="form-select"
            value={responseData[linkedIndex]?.uom ?? ''}
            onChange={onResponseChange(linkedIndex, linkedField, true)}
          >
            <option value="">Select</option>
            {relevantUoms?.length > 0
              ? [...relevantUoms].sort((a, b) => a.localeCompare(b)).map((u) => (
                <option key={u} value={u}>
                  {unitsOfMeasure?.length > 0 ? unitsOfMeasure.find((unit) => unit?.value === u)?.label : u}
                </option>
              ))
              : unitsOfMeasure?.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
        );
      },
    };
  };

  const onNextClick = () => {
    findActiveSectionInFormAndNavigateToNext({
      sections: formdata.sections,
      findNext: false
    });
  }

  const goBack = () => {
    const _ = findActiveSectionAndItsSiblingsInForm(formdata.sections);
    if (_.foundAtIndex >= 0) {
      traverseBackFromIndex(_.foundAtIndex, _.siblings);
    } else {
      initializeRootAsActiveSection(formdata);
    }
  }

  const isCheckboxChecked = (options, id) => {
    if (!options || options?.length === 0) return false;

    return options.findIndex(x => +x.id === +id) >= 0;
  };

  const checkIsFinal = () => {
    const cSection = activeSectionData?.id;
    if (formdata.sections == undefined || formdata.sections == null || formdata.sections == '') {
      return
    }
    let section = formdata.sections;
    if (section.length > 0) {
      let subSec = section[(section.length) - 1].subSections;
      if (subSec.length > 0) {
        if (cSection == subSec[subSec.length - 1].id) {
          return false;
        } else {
          return true;
        }

      } else {
        if (cSection == section[(section.length) - 1].id) {
          return false;
        } else {
          return true;
        }
      }
    } else {
      return false;
    }
  };

  const getRemarkField = (field, index, response) => {
    let responseOptions;
    let isMandatory = false;
    let showRemark = false;
    if (field.allowRemark) {
      if (field.type === 'RADIO'
        || field.type === 'MULTIPLE_CHOICE'
        || field.type === 'DROP_DOWN') {
        responseOptions = response?.responseOptions;

      } else if (field.type === 'MULTI_SELECT_DROP_DOWN') {
        responseOptions = response?.responseOptionsShow;
      }
      for (let i = 0; i < responseOptions?.length; i++) {
        const rOption = responseOptions[i];
        const twinOption = field.options?.find(opt => +opt.id === +rOption.id);
        if (twinOption?.showRemarks) {
          showRemark = true;
          isMandatory = twinOption.remarksMandatory;
          break;
        }
      }
    }
    return showRemark && <>{
      <div>
        <label className="form-check-label" htmlFor="remarktitle">{field.remarkLabel}</label>
        <div className="mb-2">
          <input className='form-control'
            type="text"
            required={isMandatory}
            value={responseData[index].remarkResponse}
            onChange={(e) => onRemarkChange(e.target.value, field, index)} />
        </div>
      </div>
    }</>
  };

  const getOtherField = (field, index, response) => {
    let isMandatory = false;
    let showOthers = false;
    if (field.allowOtherOption) {
      let responseOptions;
      if (field.type === 'RADIO'
        || field.type === 'MULTIPLE_CHOICE'
        || field.type === 'DROP_DOWN') {
        responseOptions = response?.responseOptions;
      } else if (field.type === 'MULTI_SELECT_DROP_DOWN') {
        responseOptions = response?.responseOptionsShow;
      }
      for (let i = 0; i < responseOptions?.length; i++) {
        const rOption = responseOptions[i];
        const twinOption = field.options?.find(opt => opt.id === rOption.id);
        if (twinOption?.showOthers) {
          showOthers = true;
          isMandatory = field.otherFieldValidation?.mandatory;
          break;
        }
      }
    }
    return showOthers && <>{
      <div>
        <div className="mb-2">
          <input className='form-control'
            type="text"
            required={isMandatory}
            value={responseData[index].otherResponse}
            onChange={(e) => onOtherFieldTextChange(e.target.value, field, index)}
            placeholder={field.otherTextLabel} />
        </div>
      </div>
    }</>
  };

  const onRemarkChange = (value, field, index) => {
    let data = [...responseData];
    if (data[index]) {
      data[index]['remarkResponse'] = value;
      setResponseData(data);
    }
  };

  const onOtherFieldTextChange = (value, field, index) => {
    let data = [...responseData];
    if (data[index]) {
      data[index]['otherResponse'] = value;
      setResponseData(data);
    }
  };

  const shareSection = (section) => {
    setShareModalData(section ?? activeSectionData);
    setShowShareModal(true);
  }

  const callbackShareModal = (obj) => {
    setShowShareModal(false);
  }

  const loadUnitsOfMeasure = () => {
    FormService.getUnitsOfMeasure(companyId)
      .then(res => {
        if (!res.data || res.data?.length === 0) return;

        const inputList = [];
        res.data?.forEach(element => {
          inputList.push({
            value: element.inputUnit,
            label: element.inputUnitName,
          });
        });
        const uniqueInputArray = Array.from(new Set(inputList.map(obj => obj.value))).map(
          value => inputList.find(obj => obj.value === value)
        );
        uniqueInputArray.sort((a, b) => a.value.localeCompare(b.value));
        setUnitsOfMeasure(uniqueInputArray);
      })
      .catch((error) => {
      });
  };

  const renderOptionHelpText = (opt) => {
    if (!opt.helpText) return null;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Popover id={`popover-help-${opt.id}`} className="p-2">
            <div className="p-1">{opt.helpText}</div>
          </Popover>
        }>
        <span className="ms-2 cursor-pointer">
          <BsQuestionCircle />
        </span>
      </OverlayTrigger>
    );
  };

  const renderQuestion = (item, index, questionNumber = index + 1) => {
    const responseIndex = responseData?.findIndex(r => r.formField?.id === item.id);
    if (responseIndex < 0) return <></>;

    const handleToggle = (id) => {
      setOpenPopoverId(openPopoverId === id ? null : id);
    };

    const popoverClickRootClose = (
      <Popover id="popover-trigger-click-root-close">
        <Popover.Header>
          Help
        </Popover.Header>
        <Popover.Body className=''>
          {item.fieldHelpText && (
            <p>
              <div dangerouslySetInnerHTML={{ __html: item.fieldHelpText }} />
            </p>
          )}
        </Popover.Body>
      </Popover>
    );

    const response = responseData[responseIndex];
    return (
      <div className='PS_inner_01' key={`questionField-${index}`}>
        <div className='PS_inner_Qus_ViewCard02'>
          <div className='PSS_Qus_ViewCard02'>
            <div className='PSinner_Qus_View_box02'>
              <div className='d-flex'>
                <h6>
                  <span className='me-1'>{questionNumber}.</span>
                </h6>
                <div>
                  <h6>
                    {item.name}
                    {!item.optional && <span className="text-danger h5 m-0" style={{ lineHeight: '1px' }}> *</span>}

                    {item.fieldHelpText && (
                      <OverlayTrigger
                        trigger="click"
                        rootClose
                        placement="auto"
                        overlay={popoverClickRootClose}
                        onToggle={() => handleToggle(item.id)}
                      >
                        <span className='mx-2 cursor' onClick={() => handleToggle(item.id)}>
                          <BsQuestionCircle />
                        </span>
                      </OverlayTrigger>

                    )}
                  </h6>
                </div>
              </div>

            </div>

            <div className='PSinner_Option_View_box02 3'>
              {item.type === 'RADIO' && <div>
                {item.options.length > 0 && item.options.map((opt) => (
                  <div className="form-check" key={opt.id}>
                    <input className="form-check-input"
                      type="radio"
                      name={item.name}
                      id={opt.id}
                      value={opt.id}
                      checked={response?.responseOptions[0]?.id === opt.id
                      }
                      onChange={onResponseChange(responseIndex, item)} />
                    <label className="form-check-label" htmlFor={opt.id} >
                      {opt.name}
                      {renderOptionHelpText(opt)}
                    </label>
                  </div>
                ))}
              </div>
              }
              {item.type === 'RADIO_GRID' &&
                <GridTable
                  type='PREVIEW'
                  content={item}
                />
              }
              {item.type === 'TABLE' &&
                <DataTable
                  type='BUILD'
                  content={item}
                />
              }
              {item.type === 'GRID' &&
                <GridQuestionView
                  content={item}
                  answerContext={buildGridAnswerContext()}
                />
              }
              {item.type === 'MULTI_SELECT_DROP_DOWN' &&
                <div>
                  <Multiselect
                    options={(item.options || []).map((opt) => (opt.showOthers ? { ...opt, name: opt.name || 'Other' } : opt))}
                    selectedValues={response?.responseOptionsShow}
                    onSelect={(selectedList, selectedItem) => {
                      onSelectDeSelectML(selectedList, selectedItem, responseIndex, item)
                    }}
                    onRemove={(selectedList, selectedItem) => {
                      onSelectDeSelectML(selectedList, selectedItem, responseIndex, item)
                    }}
                    displayValue="name"
                  />
                </div>
              }
              {item.type === 'MULTIPLE_CHOICE' && <div>
                {item.options.length > 0 && item.options.map((opt) => {
                  const selectedOptions = response?.responseOptions;
                  const selectedCount = selectedOptions?.length || 0;
                  const isChecked = isCheckboxChecked(selectedOptions, opt.id);
                  const shouldDisable = shouldDisableOption(item, selectedCount, isChecked);

                  return (
                    <div className="form-check" key={opt.id}>
                      <input className={`form-check-input ${!isChecked && checkValidationError(item.id) ? 'border-danger' : ''}`}
                        type="checkbox"
                        name="radopt"
                        id={opt.id}
                        value={opt.id}
                        disabled={shouldDisable}
                        checked={isChecked}
                        onChange={onResponseChange(responseIndex, item)} />
                      <label className="form-check-label" htmlFor={opt.id} >
                        {opt.name}
                        {renderOptionHelpText(opt)}
                      </label>
                    </div>
                  );
                })}
                {checkValidationError(item.id) && item.optionSelectionValidation && (
                  <div className="text-danger small mt-2">
                    {item.optionSelectionValidation.validationMessage ||
                      `Please select ${getOptionValidationMessage(item.optionSelectionValidation)}`}
                  </div>
                )}
              </div>
              }
              {item.type === 'DROP_DOWN' && <div>
                <select className='form-select w-auto'
                  value={response?.responseOptions[0]?.id}
                  onChange={onResponseChange(responseIndex, item)}>
                  <option value={null}>Select</option>
                  {item.options.length > 0 && item.options.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.name}
                    </option>
                  ))}
                </select>
              </div>
              }
              {item.type === 'RATING' && <div>
                <select className='form-select w-auto'>
                  <option>Agricultural</option>
                  <option>manufacturing</option>
                  <option>Agricultural</option>
                </select>
              </div>
              }
              {item.type === 'NUMERIC_VALUE' && renderNumericQuestion(item, response, responseIndex)}
              {renderCommentOrFileUploadIcons(item, index)}
              {item.type === 'SINGLE_TEXTBOX' && <div>
                <input type="text"
                  className="form-control"
                  value={response?.textResponse}
                  name={item.id}
                  onChange={onResponseChange(responseIndex, item)}
                />
              </div>
              }
              {item.type === 'DATE_TIME_BOX' && <div>
                <input type="date"
                  className='form-control w-auto'
                  value={response?.dateResponse}
                  name={item.id}
                  onChange={onResponseChange(responseIndex, item)}
                />
              </div>
              }
              {item.type === 'DATE_RANGE' &&
                <div>
                  <div className=" m-0 d-flex">
                    <div className="p-1">
                      <input type="date"
                        className='form-control w-auto'
                        value={response?.dateResponse}
                        max={response?.toDateResponse}
                        name={item.id}
                        id="dateResponse"
                        onChange={onResponseChange(responseIndex, item)}
                      />
                    </div>
                    <div className="p-1">
                      <input type="date"
                        className='form-control w-auto'
                        value={response?.toDateResponse}
                        name={item.id}
                        id="toDateResponse"
                        min={response?.dateResponse}
                        onChange={onResponseChange(responseIndex, item)}
                      />
                    </div>
                  </div>
                </div>
              }
              {item.type === 'TEXT_BOX' && <div>
                <textarea className='form-control'
                  rows="5"
                  placeholder='Paragraph'
                  value={response?.textResponse}
                  name={item.id}
                  onChange={onResponseChange(responseIndex, item)} >
                </textarea>
              </div>
              }
              {item.type === 'FILE_UPLOAD' && <div>
                <input type="File"
                  className='form-control'
                  onChange={(e) => null} />
                <div>
                  {
                    response?.files?.length > 0 &&
                    response?.files.map((fileDat, fileindex) => (
                      <div key={fileindex}>
                        <a onClick={() => null} key={fileindex}
                          style={{ color: 'blue', cursor: 'pointer' }}>
                          <span>{fileDat.fileName}</span>
                        </a>
                      </div>
                    ))
                  }
                </div>
              </div>
              }
              {item.type === 'SLIDER' && <div>
                <select className='form-select w-auto'>
                  <option>Agricultural</option>
                  <option>manufacturing</option>
                  <option>Agricultural</option>
                </select>
              </div>
              }
              {item.type === 'SEARCH_BOX' && <div>
                <select className='form-select w-auto'>
                  <option>Agricultural</option>
                  <option>manufacturing</option>
                  <option>Agricultural</option>
                </select>
              </div>
              }
              {item.type === 'EMAIL' && <div>
                <select className='form-select w-auto'>
                  <option>Agricultural</option>
                  <option>manufacturing</option>
                  <option>Agricultural</option>
                </select>
              </div>
              }
              {item.type === 'YES_NO' && <div>
                <select className='form-select w-auto'>
                  <option>Agricultural</option>
                  <option>manufacturing</option>
                  <option>Agricultural</option>
                </select>
              </div>
              }
              {getOtherField(item, responseIndex, response)}
              {getRemarkField(item, responseIndex, response)}
              {item.remarkMandatory && renderCommentTextArea(item, index)}
              {showComment[item.id] === true && (
                renderCommentTextArea(item, index)
              )}
              {fileUploadEnable.enabled && fileUploadEnable.itemId === item.id && renderFileUpload(item)}
              {item.fileMandatory && renderFileUpload(item, index)}
            </div>

          </div>
        </div>
      </div>
    );
  };

  const onSwitchingToRootField = (fieldId) => {
    initializeRootAsActiveSection(null);
  };

  const onSwitchingSection = (sectionId) => {
    setSectionDetails(sectionId, formdata.sections);
  };

  const onSwitchingSubSection = (subSectionId, subSections) => {
    setSectionDetails(subSectionId, subSections);
  };

  const onSwitchingBack = () => {
    goBack();
  };

  const renderLanguageSelector = (className = 'form-select w-auto') => {
    return (
      <select
        className={className}
        required
        onChange={(e) => {
          getPreviewFormDetails(e.target.value);
        }}>
        <option value="" disabled hidden>Select</option>
        {formdata?.languages?.map((item) => (
          <option value={item.id} key={item.id}>
            {item.name}
          </option>
        ))}
      </select>
    );
  };

  const renderShareIcon = (section = activeSectionData) => {
    if (formdata.reSharedForm || formdata.selfAssessment) return <></>;

    return (
      <OverlayTrigger placement="top" overlay={Utils.renderTooltip('Share Section')}>
        <span><BiShareAlt className='edit-icon' onClick={() => shareSection(section)} /></span>
      </OverlayTrigger>
    );
  };

  const getInitializedResponseArrayFor = (fields) => {
    const responseArray = JSON.parse(JSON.stringify(responseData));
    fields?.forEach(field => {
      const obj = {
        formField: {
          id: field.id
        }
      };
      if (field.type === QuestionType.MULTI_SELECT_DROP_DOWN
        || field.type === QuestionType.MULTIPLE_CHOICE
        || field.type === QuestionType.DROP_DOWN
        || field.type === QuestionType.RADIO) {
        obj['responseOptions'] = [];
      } else if (field.type === QuestionType.SINGLE_TEXTBOX
        || field.type === QuestionType.TEXT_BOX) {
        obj['textResponse'] = '';
      } else if (field.type === QuestionType.DATE_TIME_BOX) {
        obj['dateResponse'] = '';
      } else if (field.type === QuestionType.DATE_RANGE) {
        obj['dateResponse'] = '';
        obj['toDateResponse'] = '';
      } else if (field.type === QuestionType.FILE_UPLOAD) {
        obj['files'] = [];
      } else if (field.type === QuestionType.NUMERIC_VALUE) {
        obj['numericResponse'] = '';
        obj['uom'] = '';
        if (field.defaultUOM) {
          obj['uom'] = field.defaultUOM;
        }
      }
      // If a response already exists, then do not reinitialize it.
      if (responseData.find(r => r.formField?.id === field.id)) return;

      responseArray.push(obj);
    });
    return responseArray;
  };

  const findActiveSectionInFormAndNavigateToNext = ({ sections, findNext = false }) => {
    let navigated = false;
    let findNextSection = findNext;
    for (let index = 0; index < sections.length; index++) {
      const section = sections[index];
      if (findNextSection) {
        if (section.hide_alt || !section.fields || section.fields?.length === 0) {
          // If section is hidden or has no fields inside, then continue traversing
          findNextSection = true;
          const _ = findActiveSectionInFormAndNavigateToNext({
            sections: section.subSections,
            findNext: findNextSection,
          });
          navigated = _.navigated;
          findNextSection = _.findNextSection;
          if (navigated) return { navigated: navigated, findNextSection: findNextSection };
        } else {
          // If it is a visible section, then set the active section and end the traversal.
          navigated = true;
          findNextSection = false;
          setSectionDetails(section.id, sections);
          break;
        }
      } else if (section.subSections?.length > 0 || section.id === activeSectionData?.id) {
        if (section.id === activeSectionData?.id) {
          // Found the current section.
          // Now find the next section
          findNextSection = true;
        }
        const _ = findActiveSectionInFormAndNavigateToNext({
          sections: section.subSections,
          findNext: findNextSection,
        });
        navigated = _.navigated;
        findNextSection = _.findNextSection;
        if (navigated) return { navigated: navigated, findNextSection: findNextSection };
      }
    }
    return { navigated: navigated, findNextSection: findNextSection };
  };

  const findActiveSectionAndItsSiblingsInForm = (sections) => {
    let foundAt = -1, siblings = sections;
    for (let index = 0; index < sections?.length; index++) {
      const section = sections[index];
      if (section.id === activeSectionData?.id) {
        foundAt = index;
        break;
      } else if (section.subSections?.length > 0) {
        const _ = findActiveSectionAndItsSiblingsInForm(section.subSections);
        if (_.foundAtIndex >= 0) {
          foundAt = _.foundAtIndex;
          siblings = _.siblings;
          break;
        }
      }
    }
    return {
      foundAtIndex: foundAt,
      siblings: siblings
    };
  };

  const traverseBackFromIndex = (index, sections, iterateSubsections = true) => {
    let navigated = false;
    for (let i = index - 1; i >= 0; i--) {
      const section = sections[i];
      if (section.hide_alt) continue;

      if (iterateSubsections && section.subSections?.length > 0) {
        navigated = traverseBackFromIndex(section.subSections?.length, section.subSections);
        if (navigated) return true;
      } else if (section.fields?.length > 0) {
        setSectionDetails(section.id, sections);
        navigated = true;
        return true;
      } else {
        iterateSubsections = true;
      }
    }
    if (sections.length > 0 && !navigated) {
      const _ = findParentSectionAndSiblings({
        sectionToFind: sections[0].id,
        sections: formdata.sections,
        parentSiblings: [],
        parent: null
      });
      if (_.parentSiblings?.length > 0) {
        const foundIndex = _.parentSiblings?.findIndex(sib => sib.id === _.parent?.id);
        if (foundIndex >= 0) {
          // Traversal starts from one index back.
          // If the sections itself is the first one in it's parent, do not iterate its subsections again.
          // This will lead to infinite loop. To prevent this isTheFirstSectionInsideParent is used.
          const isTheFirstSectionInsideParent = _.parent.subSections?.findIndex(s => s.id === sections[0].id) === 0;
          navigated = traverseBackFromIndex(foundIndex + 1, _.parentSiblings, !isTheFirstSectionInsideParent);
        }
      }
    }
    return navigated;
  };

  /**
   * Here parent is the section inside where sectionToFind was found.
   * parentSiblings are the siblings of the parent;
   * @param {*} param0 
   * @returns 
   */
  const findParentSectionAndSiblings = ({ sectionToFind, sections, parentSiblings, parent }) => {
    let found = false;
    for (let index = 0; index < sections?.length; index++) {
      const section = sections[index];
      if (section.id === sectionToFind) {
        found = true;
        break;
      } else if (section.subSections?.length > 0) {
        const _ = findParentSectionAndSiblings({
          sectionToFind: sectionToFind,
          sections: section.subSections,
          parentSiblings: sections,
          parent: section
        });
        if (_.found) {
          return _;
        }
      }
    }
    return { found: found, parentSiblings: parentSiblings, parent: parent };
  };

  const renderCommentOrFileUploadIcons = (item, indx) => {
    if ((item.allowRemark) ||
      (item.allowFileUpload)) {
      return (
        <>

          <div className='Doc_UploadBtn 1'>
            {(item.allowRemark) && (
              <> {!(item.remarkMandatory) &&
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={`tooltip-comment-${indx}`} className="doc-action-tooltip">Add Comment</Tooltip>}
                >
                  <div
                    className='preview-action-comment'
                    role="button"
                    tabIndex={0}
                    aria-label="Add Comment"
                    onClick={() => handleCommentClick(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCommentClick(item.id);
                      }
                    }}
                  >
                    <BiMessageAltDetail />
                  </div>
                </OverlayTrigger>
              }
              </>
            )}
            {(item.allowFileUpload) && (
              <>
              {
                !(item.fileMandatory) &&
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id={`tooltip-upload-${indx}`} className="doc-action-tooltip">Upload File</Tooltip>}
                >
                  <div
                    className='preview-action-upload'
                    role="button"
                    tabIndex={0}
                    aria-label="Upload File"
                    onClick={() => handleFileUploadClick(item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleFileUploadClick(item.id);
                      }
                    }}
                  >
                    <BiUpload />
                  </div>
                </OverlayTrigger>
              }
               </>
            )}
          </div>

        </>
      )
    }
  };
  const renderCommentTextArea = (item) => {
    return (
      <>
        <div className='comment-section '>
          {item.remarkMandatory ? (
            <label className='mb-2'>
              Comment <span className='text-danger h5'>*</span>
            </label>
          ) : (
            <label className='mb-2'>Comment</label>
          )}
          {/* Render your comment input or section here */}
          <textarea className='form-control' placeholder='Add your comment'></textarea>
        </div>

      </>
    )
  }
  const renderFileUpload = (item) => {
    return (
      <div className='file-upload-section'>
        {item.fileMandatory ? (
          <label className='mb-2'>
            Upload File (Max. size: 2 MB) : Supported file formats - jpeg, pdf, xlsx, docx <span className='text-danger h5'>*</span>
          </label>
        ) : (
          <label className='mb-2'>Upload File (Max. size: 2 MB) : Supported file formats - jpeg, pdf, xlsx, docx</label>
        )}
        <input type='file' multiple accept='.jpeg,.jpg,.pdf,.xlsx,.docx' className='form-control' />
      </div>
    );
  };

  const handleCommentClick = (itemId) => {

    const d = JSON.parse(JSON.stringify(showComment));
    d[itemId] = d[itemId] !== null ? !d[itemId] : true
    setShowComment(d);
  };

  const handleFileUploadClick = (itemId) => {
    if (fileUploadEnable.itemId === itemId) {
      setFileUploadEnable({ enabled: !fileUploadEnable.enabled, itemId });
    } else {
      setFileUploadEnable({ enabled: true, itemId });
    }
  };

  const renderNumericQuestion = (item, response, responseIndex) => {
    return (
      <NumericAnswerField
        id={item.id}
        uomRequired={item.uomRequired}
        fieldValue={response?.numericResponse ?? ''}
        unitsOfMeasure={unitsOfMeasure}
        defaultUOM={item.defaultUOM}
        relevantUoms={item.relevantUoms}
        historicalDataSupported={formdata.historicalDataSupported && item.historicalDataSupported}
        historicalResponse={histResponseData}
        historicalDataYears={formdata.historicalDataYears ?? []}
        currentPeriod={new Date().getFullYear()}
        onAnswerChange={(e) => onResponseChange(responseIndex, item)(e)}
        onUOMChange={(e) => onResponseChange(responseIndex, item, true)(e)}
        onHistoricalAnswerChange={(d) => onHistResponseChange(d.value, d.year, item)}
        onHistoricalUOMChange={(d) => onHistResponseChange(d.value, d.year, item, true)} />
    )
  };

  const onHistResponseChange = (value, year, item, isUnit = false) => {
    const responseData = JSON.parse(JSON.stringify(histResponseData));
    const index = responseData[year]?.responses?.findIndex(r => r.formField?.id === item.id);
    if (item.type === QuestionType.NUMERIC_VALUE) {
      if (isUnit) {
        responseData[year].responses[index]['uom'] = value;
      } else {
        responseData[year].responses[index]['numericResponse'] = +value;
      }
    }
    setHistResponseData(responseData);
  };

  const renderQuestionCard = (item, index, fields) => (
    !item.hide_alt && (
      <div
        className={`builder-question-card${activeQuestionId === item.id ? ' builder-question-card--selected' : ''}`}
        data-field-id={item.id}
        key={`questionField-${item.id ?? index}`}
        onClick={() => onSelectQuestion(item.id)}
      >
        {renderQuestion(item, index, getVisibleQuestionNumber(fields, index))}
      </div>
    )
  );

  const renderRootQuestionsCard = () => {
    if (!formdata?.fields?.length) return null;

    const isSelected = activeSectionData?.id === 0 && !activeQuestionId;
    return (
      <div
        className={`builder-section-card builder-section-card--root${isSelected ? ' builder-section-card--selected' : ''}`}
        data-section-id={0}
      >
        <div className="builder-section-card__header">
          <span className="builder-section-card__title">Questions</span>
          <span className="builder-section-card__count">{formdata.fields.length}</span>
        </div>
        <div className="builder-section-card__body">
          <div className="builder-field-list">
            {formdata.fields.map((item, index) => renderQuestionCard(item, index, formdata.fields))}
          </div>
        </div>
      </div>
    );
  };

  const renderSectionCard = (section, isSubsection = false) => {
    if (!section || section.hide_alt) return null;

    const isSelected = activeSectionData?.id === section.id && !activeQuestionId;
    return (
      <div
        key={section.id}
        className={`builder-section-card${isSubsection ? ' builder-section-card--subsection' : ''}${isSelected ? ' builder-section-card--selected' : ''}`}
        data-section-id={section.id}
      >
        <div className="builder-section-card__header">
          <span className="builder-section-card__title">{section.title}</span>
          <span className="builder-section-card__count">{section.fields?.length || 0} questions</span>
          <div className="builder-section-card__actions" style={{ opacity: 1 }}>
            {renderShareIcon(section)}
          </div>
        </div>
        {section.description && (
          <div style={{ padding: '12px 20px' }}>
            <p className="preview-section-desc">{section.description}</p>
          </div>
        )}
        <div className="builder-section-card__body">
          <div className="builder-field-list">
            {section.fields?.map((item, index) => renderQuestionCard(item, index, section.fields))}
          </div>
          {section.subSections?.length > 0 && (
            <div className="builder-document__subsections">
              {section.subSections.map((sub) => renderSectionCard(sub, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPreviewCanvas = () => (
    <div className="builder-canvas" ref={canvasRef}>
      <div className="builder-canvas__inner">
        <div className="builder-canvas__form-header preview-form-header">
          <h1>{formdata.name}</h1>
          {formdata.description && (
            <p className="builder-text-secondary" style={{ whiteSpace: 'pre-line' }}>{formdata.description}</p>
          )}
          <ConfirmDialog
            open={confirmPublish}
            setOpen={setConfirmPublish}
            onConfirm={publishConfirm}
          >
            <div className="text-center">Are you sure want to publish?</div>
          </ConfirmDialog>
        </div>

        <div className="builder-document">
          {renderRootQuestionsCard()}
          {formdata.sections?.filter((section) => !section.hide_alt).map((section) => (
            <div className="builder-document__section-block" key={section.id}>
              {renderSectionCard(section)}
            </div>
          ))}
        </div>

        {showShareModal && shareModalData && (
          <ShareFormModal
            data={shareModalData}
            type="section"
            modalShow={showShareModal}
            callbackModal={callbackShareModal}
          />
        )}

        <FormFooter />
      </div>
    </div>
  );

  if (useNewBuilder) {
    return (
      <PreviewShell
        formdata={formdata}
        activeSectionId={activeSectionData?.id}
        activeQuestionId={activeQuestionId}
        onSelectSection={onSwitchingSection}
        onSelectSubSection={onSwitchingSubSection}
        onSelectRootFields={() => onSwitchingToRootField()}
        onSelectQuestion={onSelectQuestion}
        onPublish={() => setConfirmPublish(true)}
        canPublish={formdata?.status !== 'PUBLISHED' && !!editPermissions}
        isPublished={formdata?.status === 'PUBLISHED'}
        languageSelector={formdata?.languages?.length > 0 ? renderLanguageSelector('builder-ui-select') : null}
        userFullName={userFullName}
      >
        {formdata ? renderPreviewCanvas() : (
          <div className="builder-canvas">
            <div className="builder-canvas__inner">
              <p className="builder-text-secondary">Loading preview…</p>
            </div>
          </div>
        )}
      </PreviewShell>
    );
  }

  return (
    <div className="FormMainBody" data-testid="FormSubmit">
      <FormHeader key="previewFormKey"
        header={formdata.name}
        showWizard={true}
        totalQuestionCount={0}
        answeredQuestionCount={0}
        formData={formdata} />
      <div className='FormMainBody_main'>
        <FormSidebar key="formSidebarKey"
          name={formdata.name}
          fields={formdata.fields}
          sections={formdata.sections}
          currentSection={activeSectionData?.id}
          submittedData={null}
          onRootFieldClickCallback={(fieldId) => onSwitchingToRootField(fieldId)}
          onSectionClickCallback={(sectionId) => onSwitchingSection(sectionId)}
          onSubsectionClickCallback={(subSectionId, subSections) => onSwitchingSubSection(subSectionId, subSections)}
          validationError={null} />
        <div className='FormMainBody_SectionBox'>
          <div className='FormMainBody_SectionBox_inner01'>
            <section className='FB_Section_card'>
              <div className='FBS_head_section 2'>
                <div className='py-2 d-flex justify-content-end' >
                  {renderLanguageSelector()}
                  <div className="ps-2 ">
                    {formdata.status !== 'PUBLISHED' ? (
                      <button className='btn btn-primary px-3' onClick={() => setConfirmPublish(true)}>Publish</button>
                    ) : (
                      <div className="ribbon-4" style={{ display: !editPermissions ? 'none' : 'block' }}>
                        Published
                      </div>
                    )}
                    <ConfirmDialog
                      open={confirmPublish}
                      setOpen={setConfirmPublish}
                      onConfirm={publishConfirm}>
                      <div className='text-center'>Are you sure want to publish?</div>
                    </ConfirmDialog>
                  </div>
                </div>
                <div className='FBS_head_section_inner'>
                  <div>
                    <h4 className='m-0'>{formdata.name}</h4>
                    {formdata.description && <p className='m-0 mt-1' style={{ whiteSpace: 'pre-line' }}>{formdata.description}</p>}
                  </div>
                </div>
              </div>
              <div className='FB_Section_body'>
                <div className='FBS_body Section_ViewBox' >
                  <section className='Page_section_01 '>
                    <div className="Page_section_01_head">
                      <h6 className='text-primary m-0'>
                        {activeSectionData.title ?? formdata.name}

                        {renderShareIcon()}
                        {
                          showShareModal &&
                          <ShareFormModal data={activeSectionData}
                            type="section"
                            modalShow={showShareModal}
                            callbackModal={callbackShareModal} />
                        }
                      </h6>
                      {activeSectionData.description && <div className='m-0 text-muted' style={{ whiteSpace: 'pre-line' }}><small>{activeSectionData.description}</small></div>}
                    </div>
                    <div className='Page_section_01_body'>
                      {
                        activeSectionData?.fields?.map((item, index) => (
                          <>{!item.hide_alt && renderQuestion(item, index, getVisibleQuestionNumber(activeSectionData.fields, index))}</>
                        ))
                      }
                    </div>
                  </section>
                </div>
                <div>
                </div>
              </div>
            </section>
            <div className='PS_Qus_Card_footer p-3 text-center'>
              {
                activeSectionData.id > 0 &&
                <button className='btn btn-secondary px-4 me-2' onClick={() => onSwitchingBack()}>Back</button>
              }
              {
                checkIsFinal() &&
                <button className='btn btn-primary px-4 sbmBtn'
                  onClick={() => onNextClick()}>Next</button>
              }
            </div>
            <FormFooter />
          </div>
        </div>
      </div>
    </div>
  )
};

export default FormPreview;

